<template>
  <div
    class="flex flex-col items-start page-wrapper md:flex-row p-0 h-full flex-grow"
  >
    <section
      class="w-full h-full"
      :class="{
        'rounded-md p-24 lg:p-24 md:px-93 md:py-24': noBackground === false,
      }"
    >
      <header class="pb-10">
        <h2 class="heading-xs pb-5">Vad är ett Familjeabonnemang?</h2>
        <p class="py-4 max-w-4xl">
          Om du har eller skaffar ett mobilabonnemang med 20GB surf eller mer
          (även kallat huvudabonnemang), har du möjlighet att lägga till upp
          till fyra anslutningar för
          <span
            v-if="defaultfamilyConnectionPlanPriceReg"
            class="text-text-primary strike-through w-fit mr-4"
            >{{ defaultfamilyConnectionPlanPriceReg }} </span
          >{{ defaultfamilyConnectionPlanPrice }}
          kr/mån per familjeanslutning.
        </p>
      </header>

      <div class="pt-10 pb-4" :aria-label="infoCardsTextForScreenReaders()">
        <div class="flex justify-evenly" aria-hidden="true">
          <div
            class="w-1/2 h-auto border-border-tertiary text-center border border-solid rounded-md"
          >
            <FamilyInfoCard
              header-class="bg-surface-secondary"
              padding-x="px-0"
              padding-y="py-20"
            >
              <template #header>
                <div class="flex flex-col items-center min-h-80 gap-6">
                  <div class="flex flex-row items-end">
                    <p
                      class="text-54-54 font-xtrabold -mb-5 text-text-on-highlight"
                    >
                      <span
                        v-if="
                          mainPlan?.RegularDataAllowance &&
                          mainPlan.RegularDataAllowance * 2 ===
                            mainPlan.DataAllowance
                        "
                        class="heading-xxs strike-through w-fit"
                        >{{ mainPlan?.RegularDataAllowance }}</span
                      >
                      {{ mainPlan?.DataAllowance ?? '30' }}
                    </p>
                    <p class="font-xtrabold body-sm text-text-on-highlight">
                      GB
                    </p>
                  </div>
                  <p class="heading-xxs text-[14px] text-text-on-highlight">
                    Huvudabonnemang
                  </p>
                </div>
              </template>

              <p class="heading-xs leading-16">
                <span
                  v-if="defaultFamilyPlanPriceReg"
                  class="heading-xxs strike-through w-fit"
                  >{{ defaultFamilyPlanPriceReg }}</span
                >
                {{ defaultFamilyPlanPrice }} kr/mån
              </p>
            </FamilyInfoCard>
          </div>
          <nuxt-icon
            name="plus"
            class="text-[2.4rem] flex items-center px-5 text-text-primary"
            aria-hidden="true"
          />
          <div
            class="w-1/2 h-auto border-border-tertiary text-center border border-solid rounded-md"
          >
            <FamilyInfoCard
              header-class="bg-surface-secondary"
              padding-x="px-0"
              padding-y="py-20"
            >
              <template #header>
                <div class="flex flex-col items-center">
                  <nuxt-icon
                    name="users"
                    class="text-[32px]"
                    aria-hidden="true"
                  />
                  <p class="heading-xxs font-xtrabold text-text-on-highlight">
                    {{ familyPlan?.DataAllowance ?? '30' }} GB
                  </p>
                  <p class="heading-xxs text-[14px] text-text-on-highlight">
                    Familjeanslutning
                  </p>
                </div>
              </template>
              <p class="heading-xs leading-16">
                <span
                  v-if="defaultfamilyConnectionPlanPriceReg"
                  class="heading-xxs text-text-primary strike-through w-fit"
                  >{{ defaultfamilyConnectionPlanPriceReg }}</span
                >
                {{ defaultfamilyConnectionPlanPrice }} kr/mån
              </p>
            </FamilyInfoCard>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center">
        <div class="flex flex-col text-center mt-5 mb-20 max-w-3xl">
          <p>
            Exempel på ett
            {{ familyPlan?.DataAllowance ?? '30' }} GB huvudabonnemang med ett
            {{ familyPlan?.DataAllowance ?? '30' }} GB familjeabonnemang
            tillagd.
          </p>
          <p class="heading-xxs">
            Alla får sin egna surfmängd på
            {{ familyPlan?.DataAllowance ?? '30' }} GB var.
          </p>
        </div>
      </div>
      <AppNotification type="info" class="w-full mt-20">
        <template #header>
          <nuxt-icon
            filled
            name="alert-circle-blue"
            class="text-[2.4rem]"
            aria-hidden="true"
          />
          <h4 class="heading-xxs">En ägare för alla abonnemang</h4>
        </template>
        <p>
          Personen som står på huvudabonnemanget blir också ägare och
          betalningsansvarig för familjeabonnemangen.
          <NuxtLink
            to="https://www.halebop.se/villkor-och-prislistor"
            target="_blank"
            aria-label="Se villkor för familjeabonnemang"
            external
          >
            Se villkor.
          </NuxtLink>
        </p>
      </AppNotification>
    </section>
  </div>
</template>

<script setup lang="ts">
interface Props {
  noBackground?: boolean;
}

withDefaults(defineProps<Props>(), {
  noBackground: false,
});

const store = useFamilyStore();

const defaultFamilyPlan = computed(() => {
  return store.familyPlanBundlePricesToDisplay.find(
    (x) => x.Plan?.PlanId === store.selectedParentPlan?.planId,
  )?.Plan;
});

let mainPlan = defaultFamilyPlan;

const defaultFamilyPlanPrice = computed(() => {
  return mainPlan.value?.MonthlyFee;
});
const defaultFamilyPlanPriceReg = computed(() => {
  return mainPlan.value?.RegularMonthlyFee;
});

const defaultfamilyConnectionPlan = computed(() => {
  return store.selectedFamilyPlanBundlePrice?.Plan;
});

let familyPlan = defaultfamilyConnectionPlan;

const defaultfamilyConnectionPlanPrice = computed(() => {
  return familyPlan.value?.MonthlyFee;
});
const defaultfamilyConnectionPlanPriceReg = computed(() => {
  return familyPlan.value?.RegularMonthlyFee;
});

const infoCardsTextForScreenReaders = (): string => {
  const planPrice = defaultFamilyPlanPrice.value || 0;
  const childPlanPrice = defaultfamilyConnectionPlanPrice.value || 0;
  const sum = planPrice + childPlanPrice;

  return (
    `Illustration: ${defaultFamilyPlan.value?.DataAllowance} GB huvudabonnemang för ${planPrice} kronor i månaden, ` +
    `plus en ${defaultFamilyPlan.value?.DataAllowance} GB familjeanslutning för ${childPlanPrice} kronor i månaden, ` +
    `sammanlagt ${sum} kronor i månaden.`
  );
};

watch(store, () => {
  mainPlan = defaultFamilyPlan;
  familyPlan = defaultfamilyConnectionPlan;
});
</script>

<style scoped></style>
